import { collection, query, getDocs, where, orderBy, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from './config';
import type { Location } from '@shared/types/bike';

/**
 * Convert Firestore timestamp to Date
 */
function timestampToDate(timestamp: Timestamp | Date | null | undefined): Date {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  return timestamp.toDate();
}

/**
 * Get all active locations
 */
export async function getLocations(): Promise<Location[]> {
  const q = query(
    collection(db, 'locations'),
    where('isActive', '==', true),
    orderBy('name')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      country: data.country,
      coordinates: data.coordinates,
      phoneNumber: data.phoneNumber,
      email: data.email,
      hours: data.hours,
      amenities: data.amenities || [],
      imageUrls: data.imageUrls || [],
      partnerId: data.partnerId,
      isActive: data.isActive,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    } as Location;
  });
}

/**
 * Get locations by city
 */
export async function getLocationsByCity(city: string): Promise<Location[]> {
  const q = query(
    collection(db, 'locations'),
    where('isActive', '==', true),
    where('city', '==', city),
    orderBy('name')
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data.name,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      country: data.country,
      coordinates: data.coordinates,
      phoneNumber: data.phoneNumber,
      email: data.email,
      hours: data.hours,
      amenities: data.amenities || [],
      imageUrls: data.imageUrls || [],
      partnerId: data.partnerId,
      isActive: data.isActive,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    } as Location;
  });
}

/**
 * Search locations by name or city
 */
export async function searchLocations(searchQuery: string): Promise<Location[]> {
  const allLocations = await getLocations();
  const queryLower = searchQuery.toLowerCase();

  return allLocations.filter(
    (location) =>
      location.name.toLowerCase().includes(queryLower) ||
      location.city.toLowerCase().includes(queryLower) ||
      location.address.toLowerCase().includes(queryLower)
  );
}

/**
 * Get location by ID
 */
export async function getLocation(locationId: string): Promise<Location | null> {
  const docRef = doc(db, 'locations', locationId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    name: data.name,
    address: data.address,
    city: data.city,
    state: data.state,
    zipCode: data.zipCode,
    country: data.country,
    coordinates: data.coordinates,
    phoneNumber: data.phoneNumber,
    email: data.email,
    hours: data.hours,
    amenities: data.amenities || [],
    imageUrls: data.imageUrls || [],
    partnerId: data.partnerId,
    isActive: data.isActive,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  } as Location;
}

